<template>
	<TransitionRoot as="template" :show="open">
		<Dialog as="div" class="relative z-40" @close="emit('canceled')">
			<TransitionChild
				as="template"
				enter="ease-out duration-300"
				enter-from="opacity-0"
				enter-to="opacity-100"
				leave="ease-in duration-200"
				leave-from="opacity-100"
				leave-to="opacity-0">
				<div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
			</TransitionChild>

			<div class="fixed inset-0 z-10 w-screen overflow-y-auto">
				<div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
					<TransitionChild
						as="template"
						enter="ease-out duration-300"
						enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
						enter-to="opacity-100 translate-y-0 sm:scale-100"
						leave="ease-in duration-200"
						leave-from="opacity-100 translate-y-0 sm:scale-100"
						leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
						<DialogPanel
							class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 w-full sm:max-w-lg">
							<div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
								<div class="flex flex-col justify-start items-start">
									<div
										v-if="title != ''"
										class="flex items-center justify-start gap-3 mb-2">
										<div
											v-if="theme == 'danger'"
											class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10"
											data-testid="icon">
											<ExclamationTriangleIcon
												class="h-6 w-6 text-red-600"
												aria-hidden="true" />
										</div>

										<div
											v-if="theme == 'warning'"
											class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10"
											data-testid="icon">
											<ExclamationTriangleIcon
												class="h-6 w-6 text-yellow-600"
												aria-hidden="true" />
										</div>

										<div
											v-if="theme == 'info'"
											class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-sky-100 sm:mx-0 sm:h-10 sm:w-10"
											data-testid="icon">
											<InformationCircleIcon
												class="h-6 w-6 text-sky-600"
												aria-hidden="true" />
										</div>

										<DialogTitle
											as="h3"
											class="text-base font-semibold leading-6 text-gray-900">
											{{ title }}
										</DialogTitle>
									</div>

									<div class="text-left sm:mt-0 w-full">
										<div
											class="ps-1 text-sm text-gray-500"
											:class="title != '' ? 'sm:ml-12' : ''"
											data-testid="body">
											<slot></slot>
										</div>
									</div>
								</div>
							</div>
							<div class="bg-gray-50 px-4 py-3 flex flex-row-reverse sm:px-6 gap-3">
								<app-button
									v-if="!hideButton"
									type="button"
									:theme="theme"
									data-testid="confirm"
									@click="emit('confirmed')">
									{{ button }}
								</app-button>

								<app-button
									v-if="cancel"
									type="button"
									theme="outline"
									data-testid="cancel"
									@click="emit('canceled')">
									Cancel
								</app-button>
							</div>
						</DialogPanel>
					</TransitionChild>
				</div>
			</div>
		</Dialog>
	</TransitionRoot>
</template>

<script lang="ts" setup>
	import {
		Dialog,
		DialogPanel,
		DialogTitle,
		TransitionChild,
		TransitionRoot
	} from '@headlessui/vue'
	import { ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/vue/24/outline'

	withDefaults(
		defineProps<{
			open: boolean
			title?: string
			theme?: 'info' | 'warning' | 'danger'
			button?: string
			cancel?: boolean
			hideButton?: boolean
		}>(),
		{
			open: false,
			title: 'Confirmation',
			theme: 'danger',
			button: 'Confirm',
			cancel: true,
			hideButton: false
		}
	)

	const emit = defineEmits(['confirmed', 'canceled'])
</script>
